export type ValueFormat = "number" | "currency" | "percent" | "followers" | "frequency";

export interface ValueFormatterStrategy {
  format: (value: number) => string;
  parse: (value: string) => number;
}

const formatLargeNumber = (value: number): string => {
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(value % 1_000_000 === 0 ? 0 : 1)}M`;
  }
  if (value >= 1_000) {
    return `${(value / 1_000).toFixed(value % 1_000 === 0 ? 0 : 1)}K`;
  }
  return value.toString();
};

const parseLargeNumber = (str: string): number => {
  const cleaned = str.replace(/[£%,/mo]/gi, "").trim();
  let num = Number.parseFloat(cleaned) || 0;

  if (cleaned.toLowerCase().includes("m")) {
    num = Number.parseFloat(cleaned.replace(/m/gi, "")) * 1_000_000;
  } else if (cleaned.toLowerCase().includes("k")) {
    num = Number.parseFloat(cleaned.replace(/k/gi, "")) * 1_000;
  }

  return num;
};

class NumberFormatter implements ValueFormatterStrategy {
  format(value: number): string {
    return formatLargeNumber(value);
  }

  parse(value: string): number {
    return parseLargeNumber(value);
  }
}

class CurrencyFormatter implements ValueFormatterStrategy {
  constructor(private symbol = "£") {}

  format(value: number): string {
    return `${this.symbol}${formatLargeNumber(value)}`;
  }

  parse(value: string): number {
    return parseLargeNumber(value);
  }
}

class PercentFormatter implements ValueFormatterStrategy {
  format(value: number): string {
    return `${value}%`;
  }

  parse(value: string): number {
    return Number.parseFloat(value.replace("%", "")) || 0;
  }
}

class FollowersFormatter implements ValueFormatterStrategy {
  format(value: number): string {
    return formatLargeNumber(value);
  }

  parse(value: string): number {
    return parseLargeNumber(value);
  }
}

class FrequencyFormatter implements ValueFormatterStrategy {
  format(value: number): string {
    return `${value}/mo`;
  }

  parse(value: string): number {
    return Number.parseFloat(value.replace("/mo", "")) || 0;
  }
}

const formatters: Record<ValueFormat, ValueFormatterStrategy> = {
  number: new NumberFormatter(),
  currency: new CurrencyFormatter(),
  percent: new PercentFormatter(),
  followers: new FollowersFormatter(),
  frequency: new FrequencyFormatter(),
};

export const getFormatter = (format: ValueFormat): ValueFormatterStrategy => {
  return formatters[format];
};

export const createCurrencyFormatter = (symbol: string): ValueFormatterStrategy => {
  return new CurrencyFormatter(symbol);
};
